/**
 * Drawer latéral pour la sidebar de jeu sur petit écran (compact).
 *
 * Contient le chat de partie, le classement live, etc.
 * S'ouvre via un FAB (Floating Action Button) superposé au tapis.
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { ChatIcon, BackArrowIcon } from './Icons';

interface GameDrawerProps {
  children: ReactNode;
  unreadCount?: number;
  title?: string;
  onQuit?: () => void;
}

export default function GameDrawer({ children, unreadCount = 0, title = 'Partie', onQuit }: GameDrawerProps) {
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => setOpen(v => !v), []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, close]);

  return (
    <>
      {/* FAB */}
      <button
        className="game-drawer-toggle"
        onClick={toggle}
        aria-label="Ouvrir le panneau de jeu"
      >
        <ChatIcon size={22} />
        {unreadCount > 0 && (
          <span className="drawer-badge">{unreadCount}</span>
        )}
      </button>

      {/* Backdrop */}
      <div
        className={`game-drawer-backdrop ${open ? 'open' : ''}`}
        onClick={close}
      />

      {/* Panel */}
      <div className={`game-drawer-panel ${open ? 'open' : ''}`}>
        <div className="game-drawer-header">
          <button
            className="game-drawer-close"
            onClick={close}
            aria-label="Fermer le panneau"
          >
            <BackArrowIcon size={18} />
          </button>
          <span className="game-drawer-header-title">{title}</span>
          {onQuit && (
            <button
              className="game-drawer-quit"
              onClick={onQuit}
              title="Quitter la partie"
            >
              Quitter
            </button>
          )}
        </div>
        <div className="game-drawer-content">
          {children}
        </div>
      </div>
    </>
  );
}
